/**
 * Hearing prep checklist — the barrister edits their OWN preparation list here.
 * Whatever they set shows on every hearing's prep panel (matter home + Court Day).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, X, Scales, MapPin, ArrowCounterClockwise, FloppyDisk, CircleNotch } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  fetchPrepChecklist, savePrepChecklist, resetPrepChecklist, type PrepItem,
} from '@/lib/prepChecklistApi';

// Local row carries a stable uid for React keys; `key` is the server item key
// (empty for a new row — the server assigns one on save).
interface Row { uid: string; key: string; label: string; }
let uidSeq = 0;
const toRows = (items: PrepItem[]): Row[] => items.map((i) => ({ uid: `r${uidSeq++}`, key: i.key, label: i.label }));

export default function PrepChecklistSettingsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['prep-checklist'], queryFn: fetchPrepChecklist });

  const [prof, setProf] = useState<Row[]>([]);
  const [ops, setOps] = useState<Row[]>([]);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (data) { setProf(toRows(data.professional)); setOps(toRows(data.operational)); }
  }, [data]);

  const edit = (setter: React.Dispatch<React.SetStateAction<Row[]>>) => ({
    change: (uid: string, label: string) => setter((rs) => rs.map((r) => (r.uid === uid ? { ...r, label } : r))),
    remove: (uid: string) => setter((rs) => rs.filter((r) => r.uid !== uid)),
    add: () => setter((rs) => [...rs, { uid: `r${uidSeq++}`, key: '', label: '' }]),
  });
  const profEdit = edit(setProf);
  const opsEdit = edit(setOps);

  const clean = (rs: Row[]): PrepItem[] =>
    rs.map((r) => ({ key: r.key, label: r.label.trim() })).filter((r) => r.label);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await savePrepChecklist({ professional: clean(prof), operational: clean(ops) });
      setProf(toRows(res.professional)); setOps(toRows(res.operational));
      toast.success('Prep checklist saved');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'Could not save the checklist');
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await resetPrepChecklist();
      setProf(toRows(res.professional)); setOps(toRows(res.operational));
      toast.success('Reset to the standard checklist');
    } catch { toast.error('Could not reset'); }
    finally { setResetting(false); }
  };

  const Section = ({ title, icon, rows, e }: { title: string; icon: React.ReactNode; rows: Row[]; e: ReturnType<typeof edit> }) => (
    <Card>
      <div className="flex items-center gap-2 border-b border-border px-5 py-3">
        {icon}<span className="text-sm font-semibold text-foreground">{title}</span>
        <span className="ml-auto text-xs text-muted-foreground">{rows.filter((r) => r.label.trim()).length} items</span>
      </div>
      <CardContent className="space-y-2 p-4">
        {rows.length === 0 && <p className="px-1 py-2 text-xs text-muted-foreground">No items — add one below.</p>}
        {rows.map((r, i) => (
          <div key={r.uid} className="flex items-center gap-2">
            <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{i + 1}.</span>
            <input
              value={r.label}
              onChange={(ev) => e.change(r.uid, ev.target.value)}
              placeholder="Describe a prep step…"
              className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
            <button
              onClick={() => e.remove(r.uid)}
              className="shrink-0 rounded p-1.5 text-muted-foreground hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
              title="Remove"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        <button
          onClick={e.add}
          className="mt-1 flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:border-brand-300 hover:text-brand-600 dark:hover:text-brand-400"
        >
          <Plus className="h-3.5 w-3.5" />Add item
        </button>
      </CardContent>
    </Card>
  );

  return (
    <div className="mx-auto max-w-3xl pb-24">
      <PageHeader
        onBack={() => navigate(-1)}
        title="Hearing prep checklist"
        subtitle="Your personal preparation list — these items appear on every hearing (matter page + Court Day). Edit them to match how you prepare."
      />

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><CircleNotch className="h-5 w-5 animate-spin text-muted-foreground/40" /></div>
      ) : (
        <>
          <div className="mb-4 rounded-md border border-blue-100 bg-blue-50 dark:bg-blue-500/10 px-4 py-3 text-xs text-blue-900 dark:text-blue-200">
            <strong>How it works:</strong> renaming an item keeps its ticks on existing hearings; adding a new item starts unticked; removing one hides it going forward. Changes apply to all your hearings.
          </div>

          <div className="grid gap-4">
            <Section title="Professional" icon={<Scales className="h-4 w-4 text-brand-600 dark:text-brand-400" weight="duotone" />} rows={prof} e={profEdit} />
            <Section title="Operational" icon={<MapPin className="h-4 w-4 text-muted-foreground" weight="duotone" />} rows={ops} e={opsEdit} />
          </div>

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={handleReset} disabled={resetting || saving}
              leftIcon={resetting ? <CircleNotch className="h-4 w-4 animate-spin" /> : <ArrowCounterClockwise className="h-4 w-4" />}>
              Reset to standard
            </Button>
            <Button variant="primary" size="md" onClick={handleSave} disabled={saving || resetting}
              leftIcon={saving ? <CircleNotch className="h-4 w-4 animate-spin" /> : <FloppyDisk className="h-4 w-4" />}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
